import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Icon } from '@/lib/icons';
import type { translations } from '@/translations/translations';
import type { ThemeImportStatusFilter } from '@/api/theme-import-history.api';

interface Props {
  copy: typeof translations.fr.admin.excelFiles.themes.questions;
  search: string;
  domain: string;
  domains: string[];
  status: ThemeImportStatusFilter;
  selectedCount: number;
  canDelete: boolean;
  isChangingStatus: boolean;
  onSearchChange: (value: string) => void;
  onDomainChange: (value: string) => void;
  onStatusChange: (status: ThemeImportStatusFilter) => void;
  onAskDeactivate: () => void;
  onReactivate: () => void;
  onAskDelete: () => void;
}

export function ThemeQuestionEditorToolbar(props: Props) {
  const { copy } = props;
  const isInactive = props.status === 'inactive';
  return (
    <div className="space-y-3 rounded-xl border border-border/50 bg-background p-3 shadow-sm">
      <div role="group" aria-label={copy.statusFilter} className="inline-flex rounded-lg border border-border/60 bg-muted/30 p-1">
        {(['active', 'inactive'] as const).map((status) => (
          <button key={status} type="button" onClick={() => props.onStatusChange(status)}
            aria-pressed={props.status === status}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${props.status === status ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            {status === 'active' ? copy.activeView : copy.inactiveView}
          </button>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_13rem]">
        <div className="relative">
          <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={props.search} onChange={(event) => props.onSearchChange(event.target.value)}
            placeholder={copy.search} aria-label={copy.search} className="h-10 pl-9" />
        </div>
        <Select value={props.domain || '__all__'} onValueChange={(value) => props.onDomainChange(value === '__all__' ? '' : value)}>
          <SelectTrigger aria-label={copy.domain} className="h-10"><SelectValue placeholder={copy.allDomains} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{copy.allDomains}</SelectItem>
            {props.domains.map((domain) => <SelectItem key={domain} value={domain}>{domain}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {props.selectedCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-primary/[0.06] px-3 py-2">
          <span className="text-xs font-semibold">{copy.selected.replace('{count}', String(props.selectedCount))}</span>
          <div className="flex items-center gap-2">
            <button type="button" disabled={props.isChangingStatus}
              onClick={isInactive ? props.onReactivate : props.onAskDeactivate}
              className={`rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-40 ${isInactive ? 'bg-primary text-primary-foreground' : 'bg-destructive text-destructive-foreground'}`}>
              {isInactive ? copy.reactivateSelected : copy.deactivateSelected}
            </button>
            {props.canDelete && <button type="button" disabled={props.isChangingStatus}
              onClick={props.onAskDelete}
              className="rounded-full border border-destructive/50 px-4 py-2 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-40">
              {copy.deleteSelected}
            </button>}
          </div>
        </div>
      )}
    </div>
  );
}
